import {
  Cushion,
  FlexContainer,
  Grid,
  GridItem,
  MaxWidth,
  Rectangle,
  Shadow,
  Space,
  Typography,
} from '@pitchero/react-ui';
import { find, flatMap, reject } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Collapse } from 'react-collapse';
import Icon from '../icon';
import { Trans } from '../../lib/i18n';
import isServer from '../../lib/is-server';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import useBreakpoint from '../../lib/use-breakpoint';
import FilterIconWrapper from '../navigation/mobile/filter-icon-wrapper';
import SlashNavigation from '../navigation/mobile/slash-navigation';
import SecondaryNavigationContainer from '../navigation/secondary-navigation-container';
import SecondaryNavigationLinks from '../navigation/secondary-navigation-links';
import NewsFilters from './filters';

export const categories = ['album', 'news', 'club_event', 'match_preview', 'match_report', 'video'];

const NewsFilterBar = ({
  category,
  club,
  month,
  onChangeCategoryFilter,
  onChangeMonthFilter,
  onChangeTeamFilter,
  sections,
  t,
  teamId,
  title,
  onFilterClick,
  openFilter,
  setOpenFilter,
}) => {
  const isMobile = useBreakpoint();
  let categoryFilters = categories.map((cat) => ({
    label: t(`common:${cat}_activity_type`),
    value: cat,
  }));
  let selectedTeam;
  if (teamId) {
    selectedTeam = find(
      flatMap(sections, (section) => section.teams),
      { id: teamId },
    );
    categoryFilters = reject(categoryFilters, { value: 'club_event' });
  }

  if (!club.galleryEnabled) {
    categoryFilters = reject(
      categoryFilters,
      (filter) => filter.value === 'album' || filter.value === 'video',
    );
  }

  const onChangeTeamFilterHandler = (value) => {
    setOpenFilter(null);
    onChangeTeamFilter(value);
  };

  const onChangeCategoryFilterHandler = (value) => {
    setOpenFilter(null);
    onChangeCategoryFilter(value);
  };

  const onChangeMonthFilterHandler = (value) => {
    setOpenFilter(null);
    onChangeMonthFilter(value);
  };

  const renderFilters = () => (
    <NewsFilters
      categoryFilters={categoryFilters}
      category={category}
      club={club}
      month={month}
      openFilter={openFilter}
      sections={sections}
      selectedTeam={selectedTeam}
      setShowingMenu={() => {}}
      setOpenFilter={setOpenFilter}
      onChangeTeamFilter={onChangeTeamFilterHandler}
      onChangeCategoryFilter={onChangeCategoryFilterHandler}
      onChangeMonthFilter={onChangeMonthFilterHandler}
      teamId={teamId}
      t={t}
    />
  );

  const renderFilterControls = () => {
    if (isServer()) {
      return null;
    }

    return (
      <GridItem justifySelf="right" alignSelf="center">
        <FlexContainer>
          <Space horizontal="large">
            <FilterIconWrapper
              filterName="team"
              filterValue={selectedTeam?.name}
              onFilterClick={() => onFilterClick('team')}
            >
              <Icon name="t-shirt" size={20} />
            </FilterIconWrapper>
          </Space>
          <FilterIconWrapper
            filterName="category"
            filterValue={category}
            onFilterClick={() => onFilterClick('category')}
          >
            <Icon name="funnel" size={20} />
          </FilterIconWrapper>
          <FilterIconWrapper
            filterName="month"
            filterValue={month}
            onFilterClick={() => onFilterClick('month')}
          >
            <Icon name="calendar" size={20} />
          </FilterIconWrapper>
        </FlexContainer>
      </GridItem>
    );
  };

  const renderFilterMenu = () => (
    <Shadow>
      <Rectangle fill="#fff" borderTop="thinLightGrey" component="div">
        <Collapse isOpened={!!openFilter}>
          <MaxWidth component="div" maxWidth={1366} center>
            <Cushion
              horizontal="large"
              vertical={openFilter === 'month' ? 'medium' : 'xlarge'}
              component="div"
            >
              {renderFilters()}
            </Cushion>
          </MaxWidth>
        </Collapse>
      </Rectangle>
    </Shadow>
  );

  const renderMobileFilterMenu = () => (
    <Shadow>
      <Rectangle fill="#fff" borderTop="thinLightGrey" component="div">
        <Collapse isOpened={!!openFilter}>
          <Cushion
            horizontal="large"
            vertical={openFilter === 'month' ? 'medium' : 'xlarge'}
            component="div"
          >
            {renderFilters()}
          </Cushion>
        </Collapse>
      </Rectangle>
    </Shadow>
  );

  const links = [
    {
      name: 'News',
      route: 'news',
      params: {},
      transKey: 'news:latest_news',
    },
    {
      name: 'Calendar',
      route: 'calendar',
      params: {},
      transKey: 'common:calendar',
    },
  ];

  const renderFullBar = () => (
    <>
      <SecondaryNavigationContainer title="News & Events" fullWidth={false}>
        <FlexContainer justifyContent="space-between" alignItems="center" style={{ flex: 1 }}>
          <Cushion left={32} component="div">
            <SecondaryNavigationLinks links={links} activeSection="News" />
          </Cushion>
          {renderFilterControls()}
        </FlexContainer>
      </SecondaryNavigationContainer>
      {renderFilterMenu()}
    </>
  );

  const renderMobileBar = () => (
    <>
      <SlashNavigation
        name="News"
        nameTransKey="common:news_and_events"
        activeSection="news"
        activeSectionTransKey="news:latest_news"
        items={links}
      />
      <Cushion vertical="medium" horizontal="small">
        <Grid columns="3fr 2fr" alignItems="center">
          <Typography preset="staticSubtitle--large" size={24} weight="700" color="black">
            <Trans i18nKey="news:latest_news">{title}</Trans>
          </Typography>
          {renderFilterControls()}
        </Grid>
      </Cushion>
      {renderMobileFilterMenu()}
    </>
  );

  return <>{isMobile ? renderMobileBar() : renderFullBar()}</>;
};

NewsFilterBar.defaultProps = {
  category: null,
  month: null,
  teamId: null,
  openFilter: null,
};

NewsFilterBar.propTypes = {
  category: PropTypes.string,
  club: CLUB_PROP_TYPES.isRequired,
  month: PropTypes.string,
  onChangeCategoryFilter: PropTypes.func.isRequired,
  onChangeMonthFilter: PropTypes.func.isRequired,
  onChangeTeamFilter: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      teams: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  t: PropTypes.func.isRequired,
  teamId: PropTypes.number,
  title: PropTypes.string.isRequired,
  onFilterClick: PropTypes.func.isRequired,
  openFilter: PropTypes.string,
  setOpenFilter: PropTypes.func.isRequired,
};

export default NewsFilterBar;
